/* eslint-disable global-require, import/no-extraneous-dependencies, react/no-array-index-key */
import SortArrowIcon from '@mui/icons-material/ArrowDownward';
import {TableSortLabel} from '@mui/material';
import {DefaultScroll, LoadingSpinner, StringUtils, useCssVars, useDebounce, useDefaultScroll} from '@wallaby-ui/core';
import AgGrid from 'ag-grid-community';
import classNames from 'classnames';
import {get, isEqual} from 'lodash';
import React, {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSizeObserver} from 'src/js/hooks';

import {JustTooltipFormatter} from './formatters';
import {NoRowsToShow, TableSorting} from './MaterialTableWrapper';

export interface LTColumn<T> {
  field: string;
  title: string;
  tooltip?: string;
  render: (row: T, compatilityProps: AgGrid.ICellRendererParams, index: number) => JSX.Element | string;
  headerStyle?: CSSProperties;
  flex?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  width?: string | number;
  sorting?: boolean;
  customSort?: (row1: T, row2: T, type: any) => number;
}
const DEFAULT_HEADER_ROW_STYLE: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--dlAgGridHeaderBackground)',
  fontFamily: 'var(--dlFontFamilySemiBold)',
  letterSpacing: 'var(--dlFontLetterSpacingNormal)',
  lineHeight: 'var(--dlFontLineHeightNormal)',
  fontSize: 'var(--dlFontSizeNormal)',
  color: 'var(--dlAgGridHeaderFontColor)',
  fontWeight: 400,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
};
const HEADER_STYLE: CSSProperties = {
  minHeight: 40,
  fontWeight: 400,
  fontFamily: 'var(--dlFontFamilySemiBold)',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
};
const DEFAULT_ROW_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
};
const DEFAULT_CELL_STYLE: CSSProperties = {
  paddingInline: 11,
};
function getCellStyle<T>(column: LTColumn<T>): CSSProperties {
  const [flexGrow, flexShrink, flexBasis] = StringUtils.parseFlex(column.flex ?? 1);
  const {minWidth, maxWidth, width} = column;
  return {
    flex: `${flexGrow} ${flexShrink} ${flexBasis}`,
    width: flexBasis,
    minWidth: minWidth ?? width,
    maxWidth: maxWidth ?? width,
  };
}
export const AG_GRID_COMPATIBILITY: AgGrid.ICellRendererParams = {
  value: undefined,
  valueFormatted: undefined,
  data: undefined,
  node: undefined as unknown as AgGrid.RowNode,
  $scope: undefined,
  rowIndex: -1,
  api: undefined as unknown as AgGrid.GridApi,
  columnApi: undefined as unknown as AgGrid.ColumnApi,
  context: undefined,
  eGridCell: undefined as unknown as HTMLElement,
  eParentOfValue: undefined as unknown as HTMLElement,
  addRenderedRowListener: () => 0,
  registerRowDragger: () => 0,
};

export type DynamicChunk = {
  start: number;
  end: number;
  height?: number;
};
function deepCopyChunks(chunks: DynamicChunk[]): DynamicChunk[] {
  return chunks.map((v) => ({...v}));
}
type DynamicChunkToRender = number;
type DynamicScrollState = {
  prevScroll: number;
  scrollNode: HTMLDivElement | null;
  isInitialized: boolean;
  prevChunks: DynamicChunk[];
  chunks: DynamicChunk[];
  chunksToRender: DynamicChunkToRender[];
};
type Props<T> = {
  columns: LTColumn<T>[];
  headerRowStyle?: CSSProperties;
  data: T[] | null | undefined;
  cssVars?: Record<string, string | number | undefined>;
  defaultScroll?: DefaultScroll;
  onScroll?: (event: WheelEvent) => void;
  onScrollEnd?: () => void;
  scrollEndOffset?: number;
  defaultDynamicChunks?: DynamicChunk[];
  dynamicChunksOnChange?: (newDynamicChunks: DynamicChunk[]) => void;
  controlledSort?: TableSorting;
  onOrderChange?: (field: string, direction: 'asc' | 'desc' | undefined | null) => void;
  canSortByNone?: boolean;
  isLoading?: boolean;
  variant?: 'big'; // TODO: variants
  alignTop?: boolean;
  useDynamicRows?: boolean;
  rowsAreSingleLine?: boolean;
  chunkSize?: number;
  style?: CSSProperties;
  className?: string;
  centerLoadingSpinner?: boolean;
};
export const LightTable = <T,>(props: Props<T>) => {
  const {
    columns,
    headerRowStyle,
    data: __data,
    cssVars,
    defaultScroll,
    onScroll: outerOnScroll,
    onScrollEnd: outerOnScrollEnd,
    scrollEndOffset = 165,
    useDynamicRows,
    rowsAreSingleLine,
    chunkSize = 30,
    defaultDynamicChunks,
    dynamicChunksOnChange: outerDynamicChunksOnChange,
    controlledSort,
    onOrderChange,
    canSortByNone,
    isLoading,
    variant,
    alignTop = false,
    style,
    className,
    centerLoadingSpinner = false,
  } = props;
  const _data = useMemo(() => __data ?? [], [__data]);
  // PDH-1589: total of column floors (minWidth ?? width). Applied as min-width to the header row and body
  // rows so that, when the table overflows a narrow container, the rows (and their sticky header / striping
  // backgrounds) stretch to the full scrolled width instead of being clipped at the viewport width.
  // Columns without a width/minWidth contribute 0, so tables that shrink-to-fit are unaffected.
  const tableMinWidth = useMemo(() => {
    const px = (w?: string | number) => {
      if (w == null) return 0;
      const n = typeof w === 'number' ? w : parseFloat(w);
      return Number.isFinite(n) ? n : 0;
    };
    return columns.reduce((acc, c) => acc + px(c.minWidth ?? c.width), 0);
  }, [columns]);
  const propsRef = useRef({
    defaultScroll,
    dynamicChunksOnChange: outerDynamicChunksOnChange,
    _data,
    isMounted: true,
  });
  propsRef.current.defaultScroll = defaultScroll;
  propsRef.current.dynamicChunksOnChange = outerDynamicChunksOnChange;
  propsRef.current._data = _data;
  propsRef.current.isMounted = true;
  // dynamic rows
  const [_, __rerender] = useState(0);
  useEffect(() => {
    const state = propsRef.current;
    return () => {
      state.isMounted = false;
    };
  }, []);
  const _rerender = useCallback(() => {
    if (propsRef.current.isMounted) {
      __rerender((v) => v + 1);
    }
  }, []);
  const defaultDynScroll: DynamicScrollState = {
    prevScroll: defaultScroll?.to ?? 0,
    scrollNode: null,
    isInitialized: false,
    prevChunks: defaultDynamicChunks ?? [],
    chunks: deepCopyChunks(defaultDynamicChunks ?? []),
    chunksToRender: [],
  };
  const dynScroll = useRef(defaultDynScroll);
  const rowHeight = +StringUtils.removePrefix(`${cssVars?.rowHeight ?? 33}`, 'px');
  const minChunkSize = chunkSize * rowHeight;
  const updateDynScroll = useCallback(
    (scroll: number) => {
      if (!useDynamicRows) {
        dynScroll.current.chunks = [{start: 0, end: propsRef.current._data.length, height: 0}];
        const newChunksToRender = [0];
        if (!isEqual(newChunksToRender, dynScroll.current.chunksToRender)) {
          dynScroll.current.chunksToRender = newChunksToRender;
          _rerender();
        }
        return;
      }
      // get rendered chunk heights
      dynScroll.current.chunksToRender.forEach((i) => {
        const {scrollNode, chunks} = dynScroll.current;
        const chunk = chunks[i];
        if (!scrollNode) return;
        const chunkNode = scrollNode.querySelector(`[data-name="dynamicChunk"][data-start="${chunk.start}"]`);
        if (chunkNode) {
          chunk.height = chunkNode.getBoundingClientRect().height;
        }
      });
      const {chunks, prevScroll} = dynScroll.current;
      if (JSON.stringify(chunks) !== JSON.stringify(dynScroll.current.prevChunks)) {
        const chunksCopy = deepCopyChunks(chunks);
        if (propsRef.current.dynamicChunksOnChange) propsRef.current.dynamicChunksOnChange(chunksCopy);
        dynScroll.current.prevChunks = chunksCopy;
      }
      // find start chunk
      const isScrollingUp = scroll < prevScroll;
      dynScroll.current.prevScroll = scroll;
      const findStartIndex = (offset: number = 0) => {
        let matchBottom = 0;
        const matchIndex = chunks.findIndex((v) => {
          matchBottom += v.height ?? minChunkSize;
          if (matchBottom + offset >= scroll) return true;
          return false;
        });
        return matchIndex === -1 ? chunks.length : matchIndex;
      };
      let startIndex = 0;
      const prevStartIndex = dynScroll.current.chunksToRender[0];
      if (isScrollingUp) {
        startIndex = findStartIndex();
        if (startIndex > prevStartIndex) return;
      } else {
        const {scrollNode} = dynScroll.current;
        const tableHeight = scrollNode?.getBoundingClientRect().height ?? 0;
        const tableHeadHeight = scrollNode?.querySelector('.MuiTableHead-root')?.getBoundingClientRect().height ?? 0;
        startIndex = findStartIndex(-tableHeight + tableHeadHeight) - 1;
        if (startIndex < prevStartIndex + 1) return;
      }
      const maxStartIndex = Math.floor((propsRef.current._data.length - chunkSize - 1) / chunkSize);
      startIndex = Math.max(0, Math.min(startIndex, maxStartIndex)); // clamp
      // get chunksToRender
      const newChunksToRender: number[] = [];
      const addChunkToRender = (chunkIndex: number) => {
        newChunksToRender.push(chunkIndex);
        if (chunkIndex >= chunks.length) {
          const lastChunk = chunks[chunks.length - 1] ?? {start: 0, end: 0};
          chunks.push({
            start: lastChunk.end,
            end: lastChunk.end + chunkSize,
          });
        }
      };
      addChunkToRender(startIndex);
      const needSecondChunk = chunks[startIndex].end < propsRef.current._data.length;
      if (needSecondChunk) {
        addChunkToRender(startIndex + 1);
      }
      // rerender
      if (!isEqual(newChunksToRender, dynScroll.current.chunksToRender)) {
        dynScroll.current.chunksToRender = newChunksToRender;
        window.setTimeout(() => {
          _rerender();
        });
        const {scrollNode} = dynScroll.current;
        if (scrollNode) {
          const hidableLoadingSpinner = (scrollNode.parentElement as HTMLDivElement).querySelector(
            '[data-name="hidableLoadingSpinner"]'
          ) as HTMLDivElement | null;
          if (hidableLoadingSpinner) {
            hidableLoadingSpinner.style.transition = 'opacity 1s cubic-bezier(.6, 0, .8, 1)';
            hidableLoadingSpinner.style.opacity = '1';
          }
        }
      }
    },
    [useDynamicRows, minChunkSize, chunkSize, _rerender]
  );
  const resetDynScroll = useCallback(() => {
    dynScroll.current.chunks = [];
    dynScroll.current.chunksToRender = [];
    const {scrollNode} = dynScroll.current;
    if (scrollNode) scrollNode.scrollTo(0, 0);
    updateDynScroll(0);
  }, [updateDynScroll]);
  useEffect(() => {
    if (isLoading) return;
    if (useDynamicRows) {
      if (!dynScroll.current.isInitialized) {
        dynScroll.current.isInitialized = true;
        const hasStaleChunks = dynScroll.current.chunks.some(
          (c) => c.start >= propsRef.current._data.length
        );
        if (hasStaleChunks) {
          resetDynScroll();
        } else {
          updateDynScroll(propsRef.current.defaultScroll?.to ?? 0);
        }
      } else {
        resetDynScroll();
      }
    } else {
      dynScroll.current.chunks = [];
      dynScroll.current.chunksToRender = [];
      updateDynScroll(0);
    }
  }, [useDynamicRows, isLoading, updateDynScroll, resetDynScroll, _data]);
  // onScroll
  const prevOnScroll = useRef(null as ((event: WheelEvent) => void) | null);
  const onScrollEndDebounce = useDebounce(() => outerOnScrollEnd && outerOnScrollEnd(), 400);
  const onScroll = useCallback(
    (event: WheelEvent) => {
      const node = event.target as HTMLDivElement;
      const {scrollTop} = node;
      const scrollHeight = node.scrollHeight - node.clientHeight;
      if (useDynamicRows) updateDynScroll(scrollTop);
      if (outerOnScroll) outerOnScroll(event);
      if (scrollTop + scrollEndOffset >= scrollHeight) onScrollEndDebounce();
    },
    [useDynamicRows, updateDynScroll, outerOnScroll, scrollEndOffset, onScrollEndDebounce]
  );
  // on resize
  const prevWidth = useRef(undefined as number | undefined);
  const {setRef: sizeRef, width} = useSizeObserver({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    if (rowsAreSingleLine) return;
    if (prevWidth.current && width !== prevWidth.current) {
      resetDynScroll();
    }
    prevWidth.current = width;
  }, [rowsAreSingleLine, width, resetDynScroll]);
  // cssVars
  const {__ref: _tableRef, setRef: setTableRef} = useCssVars({
    rowBackgroundEven: 'transparent',
    rowBackgroundOdd: 'var(--dlAgGridRowStripping)',
    rowBackgroundHover: 'var(--dlAgGridColumnHover)',
    rowHeight,
    rowFontFamily: 'var(--dlFontFamilyNormal)',
    ...cssVars,
  });
  useDefaultScroll(_tableRef, defaultScroll);
  if (isLoading) {
    const wrapperStyles: CSSProperties = centerLoadingSpinner ? {flexGrow: 1, alignContent: 'center'} : {};
    return <LoadingSpinner isVisible={isLoading} marginTop={0} wrapperStyles={wrapperStyles} />;
  }
  const getTableRow = (rowIndex) => {
    const row = _data[rowIndex];
    return row != null ? (
      <LightTableRow key={rowIndex} row={row} i={rowIndex} columns={columns} minWidth={tableMinWidth || undefined} />
    ) : (
      <React.Fragment key={rowIndex} />
    );
  };
  const {chunks, chunksToRender} = dynScroll.current;
  const lastChunk = chunks[chunks.length - 1];
  let dynamicBodyHeight: number | undefined;
  let padBottom: number | undefined;
  if (useDynamicRows) {
    dynamicBodyHeight = chunks.reduce((acc, v) => acc + (v.height ?? 0), 0);
    const haveAllChunks = (lastChunk?.end ?? 0) >= _data.length;
    if (!haveAllChunks) {
      dynamicBodyHeight = Math.max(dynamicBodyHeight, window.innerHeight);
      padBottom = minChunkSize + 1;
    }
  }
  return (
    <div
      ref={sizeRef}
      className={`${className ? `${className} ` : ''}ag-body-viewport light-table`}
      data-variant={variant}
      data-align-top={alignTop}
      data-use-dynamic-rows={useDynamicRows}
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 8,
        ...(style ?? {}),
      }}
    >
      <div
        ref={(node) => {
          setTableRef(node);
          if (node) {
            const didntHaveScrollNode = dynScroll.current.scrollNode == null;
            dynScroll.current.scrollNode = node;
            if (didntHaveScrollNode) _rerender();
            node.removeEventListener('scroll', prevOnScroll.current as any);
            node.addEventListener('scroll', onScroll as (event: Event) => any, {passive: true});
            prevOnScroll.current = onScroll;
          }
        }}
        data-name='ag-filetable-content'
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          overflow: 'auto',
        }}
      >
        <div
          className='MuiTableHead-root'
          style={{
            ...DEFAULT_HEADER_ROW_STYLE,
            ...(headerRowStyle ?? {}),
            minWidth: tableMinWidth || undefined,
          }}
        >
          {columns.map((column, j) => {
            const headerContent = (
              <JustTooltipFormatter
                {...AG_GRID_COMPATIBILITY}
                value={column.title}
                tooltipOverride={column.tooltip ?? column.title}
                style={{fontFamily: HEADER_STYLE.fontFamily, padding: 0}}
              />
            );
            return (
              <div
                role='columnheader'
                key={j}
                col-id={column.field}
                style={{
                  ...HEADER_STYLE,
                  ...(column.headerStyle ?? {}),
                  ...getCellStyle(column),
                }}
              >
                {column.sorting ? (
                  <TableSortLabel
                    IconComponent={SortArrowIcon}
                    active={controlledSort?.field === column.field}
                    direction={controlledSort?.field === column.field ? controlledSort?.direction || 'asc' : 'asc'}
                    onClick={() => {
                      let newDirection;
                      if (controlledSort?.field !== column.field) {
                        newDirection = 'asc';
                      } else if (controlledSort.direction === 'asc') {
                        newDirection = 'desc';
                      } else if (controlledSort.direction === 'desc') {
                        newDirection = canSortByNone ? undefined : 'asc';
                      }
                      if (onOrderChange) onOrderChange(newDirection ? column.field : '', newDirection);
                      if (useDynamicRows) {
                        resetDynScroll();
                      }
                    }}
                  >
                    {headerContent}
                  </TableSortLabel>
                ) : (
                  headerContent
                )}
              </div>
            );
          })}
        </div>
        {_data.length === 0 ? (
          <div className='MuiTableBody-root' style={{width: '100%', height: '100%'}}>
            <NoRowsToShow />
          </div>
        ) : (
          <div
            className='MuiTableBody-root'
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              minHeight: dynamicBodyHeight,
            }}
          >
            {useDynamicRows ? (
              <div
                style={{
                  width: '100%',
                  minHeight: chunks.slice(0, chunksToRender[0] ?? 0).reduce((acc, v) => acc + (v.height ?? 0), 0),
                }}
              />
            ) : undefined}
            {chunksToRender.map((chunkIndex) => {
              const chunk = chunks[chunkIndex];
              // const needBorders = _data.length > 2 * chunkSize;
              // const needTopBorder = needBorders && chunkIndex !== 0;
              // const needBottomBorder = needBorders && chunk.end < _data.length;
              return (
                <div
                  key={chunk.start}
                  data-name='dynamicChunk'
                  data-start={chunk.start}
                  style={{
                    width: '100%',
                    // borderTop: needTopBorder ? '1px solid var(--dlAgGridChunkBorder)' : undefined,
                    // borderBottom: needBottomBorder ? '1px solid var(--dlAgGridChunkBorder)' : undefined,
                  }}
                >
                  {Array(chunk.end - chunk.start)
                    .fill(0)
                    .map((_, i) => getTableRow(chunk.start + i))}
                </div>
              );
            })}
            {useDynamicRows ? (
              <div
                style={{
                  width: '100%',
                  minHeight: padBottom,
                }}
              />
            ) : undefined}
          </div>
        )}
      </div>
      {useDynamicRows ? (
        <div
          ref={(node) => {
            if (node) {
              node.style.transition = 'opacity 0.1s'; // eslint-disable-line
              node.style.opacity = '0'; // eslint-disable-line
            }
          }}
          data-name='hidableLoadingSpinner'
          style={{
            transition: '',
            opacity: 0,
            position: 'absolute',
            zIndex: 2,
            top: 0,
            display: 'flex',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            background: 'rgba(0, 0, 0, 0.3)',
            alignItems: 'center',
          }}
        >
          <LoadingSpinner isVisible marginTop={0} data-hidable='true' />
        </div>
      ) : undefined}
    </div>
  );
};
LightTable.displayName = 'LightTable';

type RowProps<T> = {
  row: T;
  i: number;
  columns: LTColumn<T>[];
  minWidth?: number;
};
const LightTableRow = <T,>({row, i, columns, minWidth}: RowProps<T>) => {
  return (
    <div role='row' row-index={i} row-odd={(i % 2 === 1).toString()} style={{...DEFAULT_ROW_STYLE, minWidth}}>
      {columns.map((column, j) => {
        return (
          <div
            role='gridcell'
            key={j}
            col-id={column.field}
            style={{
              ...DEFAULT_CELL_STYLE,
              ...getCellStyle(column),
            }}
          >
            {column.render(row, AG_GRID_COMPATIBILITY, i)}
          </div>
        );
      })}
    </div>
  );
};

export function useLightTableSort<T>(data: T[], columns: LTColumn<T>[], sortingList: TableSorting[]): T[] {
  const sortedData = useMemo(
    () => {
      const dataCopy = [...data];
      const defaultSort = (field: string) => (row1: T, row2: T) => {
        const key = (row: T) => get(row, field);
        return StringUtils.compare(key(row1), key(row2));
      };
      const compare = (field: string, row1: T, row2: T) => {
        const compareField = columns.find((v) => v.field === field)?.customSort ?? defaultSort(field);
        return compareField(row1, row2, 'row');
      };
      return dataCopy.sort((row1, row2) => {
        const comparisons: number[] = [
          ...sortingList.map((sorting) => {
            if (!sorting.field || !sorting.direction) return 0;
            const comparison = compare(sorting.field as string, row1, row2);
            return sorting.direction === 'asc' ? comparison : -comparison;
          }),
        ];
        return comparisons.reduce((acc, v) => acc || v, 0);
      });
    }, // eslint-disable-next-line
    [JSON.stringify({data, columns, sorting: sortingList})]
  );
  return sortedData;
}
export function getTableMaxHeight(rowCount: number): number {
  return 46 * (1 + rowCount);
}
